/**
 * Shared catalog option lists for the template creation Wizard and the TemplateEditor
 * details form, so both offer the same Category choices (spec 022 / #4, #6).
 */

/** Product categories (cloud `product_type`). */
export const PRODUCT_CATEGORIES = [
  { value: "tpu_phone_case", label: "Phone case (TPU)" },
  { value: "tablet_case", label: "Tablet case" },
  { value: "laptop_sleeve", label: "Laptop sleeve" },
  { value: "accessory", label: "Accessory" },
] as const;

/** Structured logger seam (matches AppLogger.log) — optional so consumers/tests can omit it. */
export interface CatalogLogger {
  log(level: string, message: string, ctx?: Record<string, unknown>): void;
}

/** Outcome of a catalog list fetch: the data (empty on failure) plus the error, never swallowed. */
export interface CatalogFetchResult<T> {
  data: T[];
  error: Error | null;
}

/**
 * Run a single catalog list fetch, CAPTURING (never swallowing) any failure so the
 * caller can surface a "couldn't load" state + retry instead of a silently-empty
 * dropdown (Task 12 / C4). The failure is logged with structured detail (the list
 * name + the error) via the optional logger so it also lands in the observability
 * trail. On success `error` is null; on failure `data` is `[]` and `error` is set.
 */
export async function loadCatalogList<T>(
  name: string,
  fetcher: () => Promise<T[]>,
  logger?: CatalogLogger,
): Promise<CatalogFetchResult<T>> {
  try {
    return { data: await fetcher(), error: null };
  } catch (e) {
    const error = e instanceof Error ? e : new Error(String(e));
    logger?.log("error", `Catalog fetch failed — ${name} list didn't load`, { list: name, error });
    return { data: [], error };
  }
}
